import React, { useMemo, useState } from 'react';
import { Box } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent, useMultipleEmployerSelectAvailable } from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import { ReportSummaryBase, ReportSummaryWrapper } from '../ReportSummary';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import CardStatusDateRangeSelector from './CardStatusDateRangeSelector';
import { CardStatusSummaryReportParams } from './CardStatusReport.types';
import { useCardStatusReportExportSummaryCSVQuery, useCardStatusReportSummaryQuery } from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: CardStatusSummaryReportParams;
  onSetFilterValue: FilterSetters<CardStatusSummaryReportParams>;
}

const CardStatusReportSummary: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const {
    isLoading,
    data,
    lastTimeUpdateDate,
    title,
    isEmptyData,
    isLoadingMore,
    isHasNextPage,
    handleLoadMore,
  } = useCardStatusReportSummaryQuery({
    ...filtersValues,
    isMultipleEmployer: isMultipleEmployerAvailable,
  });
  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useCardStatusReportExportSummaryCSVQuery({
      ...filtersValues,
      isMultipleEmployer: isMultipleEmployerAvailable,
    });
  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }
    if (!isLoading && !data.length) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }
    return (
      <ReportSummaryBase
        isPreviewMode={isPreviewMode}
        subTitle={title}
        isLoading={isLoading}
        data={data}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        isLoadingMore={isLoadingMore}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersValues.employerId, filtersValues.employerIds, isLoading, isPreviewMode, title]);
  return (
    <TopButtonPortalWrapper
      isPreviewMode={isPreviewMode}
      infoNode={
        <ReportLastUpdateDate
          isLoading={isLoading}
          key={`${filtersValues.employerId}`}
          value={lastTimeUpdateDate}
        />
      }
      buttonNode={
        <Box direction="row">
          <AddScheduledReportButton
            employerId={filtersValues.employerId}
            employerIds={filtersValues.employerIds}
            isMultipleEmployer={isMultipleEmployerAvailable}
            type={ReportTypeKeys.CardStatus}
            tab={ReportTab.Summary}
          />
          <Box margin={{ left: 'spacing12' }}>
            <ExportButton
              onClick={handleExportCSV}
              isLoading={isExportLoading}
              disabled={!isEmployerIsPresent || isEmptyData}
              text="Download Summary (CSV)"
            />
          </Box>
        </Box>
      }
    >
      <ReportSummaryWrapper
        isPreviewMode={isPreviewMode}
        filters={
          <Box fill direction="row">
            <Box width="290px">
              {isMultipleEmployerAvailable ? (
                <MultipleEmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  values={filtersValues.employerIds}
                  onChangeValues={onSetFilterValue.employerIds}
                />
              ) : (
                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />
              )}
            </Box>
            {!isPreviewMode && (
              <Box width="290px" margin={{ left: 'spacing12' }}>
                <CardStatusDateRangeSelector
                  disabled={!isEmployerIsPresent}
                  value={filtersValues.cardStatusGroupType}
                  onChangeValue={onSetFilterValue.cardStatusGroupType}
                />
              </Box>
            )}
          </Box>
        }
      >
        {childrenNode}
      </ReportSummaryWrapper>
    </TopButtonPortalWrapper>
  );
};

export default CardStatusReportSummary;
